import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import { IOperacaoPatioSPE } from "../../types/types";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IOperacaoPatioSPE;
  onConfirm: () => void;
  onClose: () => void;
}

interface FormValues {
  id_operacao_porto_agendada_SPE: string;
  id_operacao_patio_entrada_veiculo_SPE: string;
  searchQueryEntrada: string;
  searchQueryAgendada: string;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [agendamentosSPE, setAgendamentosSPE] = useState<{ value: any; label: string }[]>([]);
  const [entradasSPE, setEntradasSPE] = useState<{ value: any; label: string }[]>([]);
  const { user } = useAuth();

  const idOperacaoPatioAtual = props.selectedRow?.id_operacao_patio_SPE;

  const getEntradasSPE = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      const body: any = {
        qtd_por_pagina: 100,
        order_by: "id_operacao_patio_entrada_veiculo",
        order_direction: "desc",
        ativo: true,
        // Só entradas ainda não associadas a uma operação pátio SPE
        disponivel_associacao: true,
        id_operacao_patio_SPE_atual: idOperacaoPatioAtual,
      };
      if (search && search.trim().length > 0) {
        body.placa_dianteira = search.toUpperCase().trim();
      }

      const response = await api.post("/listar/entradaVeiculosSpe", body);
      const data = response.data?.data || response.data || [];

      const list = data.map((item: any) => ({
        value: String(item.id_operacao_patio_entrada_veiculo_SPE || item.id_operacao_patio_entrada_veiculo),
        label: `Entrada ID ${item.id_operacao_patio_entrada_veiculo_SPE || item.id_operacao_patio_entrada_veiculo} - Placa: ${item.placa_dianteira || 'N/A'} - Data: ${item.data_hora || 'N/A'}`,
      }));
      setEntradasSPE(list);
    } catch (err) {
      console.error("Erro ao listar Entradas SPE:", err);
    } finally {
      setLoading(false);
    }
  }, [idOperacaoPatioAtual]);

  const getAgendamentosSPE = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      const body: any = {
        qtd_por_pagina: 100,
        order_by: "data_agendamento_terminal",
        order_direction: "asc",
        ativo: true,
        // Só agendamentos pendentes e ainda não associados a uma operação pátio SPE
        disponivel_associacao: true,
        id_operacao_patio_SPE_atual: idOperacaoPatioAtual,
      };
      if (search && search.trim().length > 0) {
        body.placa = search.toUpperCase().trim();
      }

      const response = await api.post("/listar/operacaoAgendadaSpe", body);
      const data = response.data?.data || response.data || [];

      const list = data.map((item: any) => ({
        value: String(item.id_operacao_porto_agendada_SPE),
        label: `Agendamento ID ${item.id_operacao_porto_agendada_SPE} - Placa: ${item.placa_dianteira_veiculo || 'N/A'} - Agendado: ${item.data_agendamento_terminal || 'N/A'} - Carga: ${item.tipo_carga || 'N/A'}`,
      }));
      setAgendamentosSPE(list);
    } catch (err) {
      console.error("Erro ao listar Agendamentos SPE:", err);
    } finally {
      setLoading(false);
    }
  }, [idOperacaoPatioAtual]);

  useEffect(() => {
    getEntradasSPE();
    getAgendamentosSPE();
  }, [getEntradasSPE, getAgendamentosSPE]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!values.id_operacao_patio_entrada_veiculo_SPE) {
        FrontendNotification("Selecione a Entrada Associada à Triagem.", "error");
        return;
      }
      if (!values.id_operacao_porto_agendada_SPE) {
        FrontendNotification("Selecione o Agendamento no Porto.", "error");
        return;
      }

      try {
        setLoading(true);

        const body: any = {
          id_operacao_porto_agendada_SPE: Number(values.id_operacao_porto_agendada_SPE),
          id_operacao_patio_entrada_veiculo_SPE: Number(values.id_operacao_patio_entrada_veiculo_SPE),
          id_operacao_patio_entrada_veiculo: Number(values.id_operacao_patio_entrada_veiculo_SPE),
          status: 2, // 2 = No Pátio
          ativo: true,
          id_usuario_historico: user?.id,
        };

        if (props.isEdit && props.selectedRow?.id_operacao_patio_SPE) {
          body.id_operacao_patio_SPE = props.selectedRow.id_operacao_patio_SPE;
          await api.post("/atualizar/operacaoPatioSpe", body);
          FrontendNotification("Associação da Triagem SPE atualizada com sucesso!", "success");
        } else {
          await api.post("/cadastrar/operacaoPatioSpe", body);
          FrontendNotification("Triagem SPE associada com sucesso! Status alterado para No Pátio.", "success");
        }

        setLoading(false);
        props.onConfirm();
      } catch (error: any) {
        setLoading(false);
        const msg = error?.response?.data?.error_msg || "Erro ao realizar a associação da Triagem SPE.";
        FrontendNotification(msg, "error");
      }
    },
    [props, user]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      id_operacao_porto_agendada_SPE: props.selectedRow?.id_operacao_porto_agendada_SPE ? String(props.selectedRow.id_operacao_porto_agendada_SPE) : "",
      id_operacao_patio_entrada_veiculo_SPE: (props.selectedRow as any)?.id_operacao_patio_entrada_veiculo_SPE ? String((props.selectedRow as any).id_operacao_patio_entrada_veiculo_SPE) : (props.selectedRow?.id_operacao_patio_entrada_veiculo ? String(props.selectedRow.id_operacao_patio_entrada_veiculo) : ""),
      searchQueryEntrada: "",
      searchQueryAgendada: "",
    },
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="w-full flex flex-col min-h-[420px]">
      <Loading loading={loading} skeletonFields={2} skeletonColumns={1} skeletonFooterButtons={1} />

      {/* Conteúdo Cinza exatamente como na imagem de referência */}
      <div className="bg-[#EAE6E6] p-6 flex-1 flex flex-col gap-5 rounded-t-lg">

        {/* Bloco 1: Entrada do Veículo no Pátio */}
        <div>
          <span className="text-base font-bold text-gray-900 block mb-3">
            Entrada do Veículo no Pátio
          </span>
          <span className="text-sm font-semibold text-gray-700 block mb-2">
            Entrada Associada à Triagem
          </span>
          
          <div className="max-w-md">
            <SelectCustom
              id="id_operacao_patio_entrada_veiculo_SPE"
              title=""
              data={entradasSPE}
              value={entradasSPE.find(e => String(e.value) === String(formik.values.id_operacao_patio_entrada_veiculo_SPE))}
              onChange={(opt: any) => formik.setFieldValue("id_operacao_patio_entrada_veiculo_SPE", opt ? String(opt.value) : "")}
              placeholder="Selecione"
              disabled={props.isView}
            />
          </div>
        </div>

        {/* Linha Divisória */}
        <div className="w-full h-[1px] bg-[#DBDEDF] my-1" />

        {/* Bloco 2: Operação no Porto */}
        <div>
          <span className="text-base font-bold text-gray-900 block mb-3">
            Operação no Porto
          </span>

          {/* Seleção do Agendamento SPE */}
          <div className="max-w-md">
            <span className="text-sm font-semibold text-gray-700 block mb-2">
              Agendamento SPE
            </span>
            <SelectCustom
              id="id_operacao_porto_agendada_SPE"
              title=""
              data={agendamentosSPE}
              value={agendamentosSPE.find(a => String(a.value) === String(formik.values.id_operacao_porto_agendada_SPE))}
              onChange={(opt: any) => formik.setFieldValue("id_operacao_porto_agendada_SPE", opt ? String(opt.value) : "")}
              placeholder="Selecione"
              disabled={props.isView}
            />
          </div>
        </div>

      </div>

      {/* Roda-pé com botão de Ação "Associar" no canto direito (sem botão combustível) */}
      {!props.isView && (
        <div className="bg-white p-4 flex justify-end items-center border-t border-gray-200">
          <button
            type="submit"
            className="px-8 py-3 bg-[#062D4E] hover:bg-[#041d33] text-white font-bold text-sm rounded-full transition-colors shadow-md"
          >
            Associar
          </button>
        </div>
      )}
    </form>
  );
};

export default Form;
